import { adoptUnsharedCopies } from './projectStore';
import { reconcileSharedRow, stopSharing } from './sharedProjectWrite';
import { sharedProjectData } from './sharedProjectModel';
export { sharedProjectData } from './sharedProjectModel';
export { SAVE_ATTEMPTS } from './sharedProjectWrite';
import { queueProjectActivity } from './projectActivityRemote';
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { onSignOut } from '../lib/signOutReset';
import type {
  Project,
  ProjectMember,
  ProjectInvitation,
  MemberRole,
  InvitableRole,
} from '../types';
import { convertLegacyMilestones } from '../utils/projectMilestones';

/**
 * Project collaboration — the client data layer over the relational collab
 * tables (migrations 0045 + 0214 + 0215). See docs/COLLABORATION_PLAN.md.
 *
 * The app persists PRIVATE projects in the user_projects blob (projectStore,
 * unchanged). A project becomes SHARED by promotion to a relational `projects`
 * row whose `data` jsonb holds the whole client Project, so members read/write
 * it under membership RLS. This store owns that shared slice + members +
 * invitations + assignments; the UI merges it with the blob for display.
 *
 * Fail-soft: if migrations 0214/0215 aren't run yet (the `data` column or the
 * triggers are missing) every read degrades to empty rather than throwing, so
 * the Projects page keeps working on private projects alone.
 */

/**
 * Merge shared projects into my private ones for display: append the shared
 * projects I don't already have (a project I OWN is in my blob and wins; a
 * project shared WITH me is only here). Pure, so the ProjectsView can call it and
 * a test can pin it.
 */
export function mergeSharedProjects(mine: Project[], shared: Project[]): Project[] {
  const ids = new Set(mine.map(p => p.id));
  return [...mine, ...shared.filter(s => !ids.has(s.id))];
}

// Postgres/PostgREST codes meaning "schema isn't migrated yet" — stay empty.
const UNMIGRATED = new Set(['42P01', 'PGRST205', '42703']);
const isUnmigrated = (error: { code?: string } | null | undefined) =>
  !!error?.code && UNMIGRATED.has(error.code);

interface Profile { name: string; handle: string | null; avatar: string | null; }

/** What the store knows about a shared project beyond its content. */
export interface SharedMeta {
  revision?: number;
  ownerId: string;
  workspaceId?: string;
  isOwner: boolean;
  myRole: MemberRole;
}

/** A task's assignment row (project_task_assignments) — the member-writable
 *  completion record, separate from the owner-set ProjectTask.assigneeId. */
export interface TaskAssignmentRow {
  taskId: string;
  assignedTo: string;
  assignedBy: string;
  completedAt: string | null;
}

interface CollaborationState {
  /** Projects shared WITH me or BY me (hydrated from the relational `data`). */
  sharedProjects: Project[];
  /** projectId → sharing facts (owner, my role). */
  sharedMeta: Record<string, SharedMeta>;
  /** projectId → its members (profiles hydrated). */
  membersByProject: Record<string, ProjectMember[]>;
  /** Pending invitations addressed to me. */
  incomingInvitations: ProjectInvitation[];
  /** Invitations I've sent (any status). */
  outgoingInvitations: ProjectInvitation[];
  isLoading: boolean;
  error: string | null;

  /** Load every shared project + its members. */
  fetchShared: () => Promise<void>;
  /** Load pending-to-me + sent-by-me invitations. */
  fetchInvitations: () => Promise<void>;

  /** Promote a private project to shared (I become its owner). */
  shareProject: (project: Project, workspaceId?: string) => Promise<boolean>;
  /** Demote back to private-only (deletes the relational row + its members). */
  unshareProject: (projectId: string) => Promise<void>;

  /** Invite by email at a role; the invitee accepts in-app or via email later. */
  invite: (projectId: string, email: string, role: InvitableRole, message?: string) => Promise<void>;
  /** Invite an existing connection by user id (→ in-app inbox + notification +
   *  email). `email` is the connection's known address if any (else resolved). */
  inviteMember: (projectId: string, userId: string, email: string | null, role: InvitableRole) => Promise<void>;
  /** Accept (→ membership is created by the 0215 trigger) or reject an invite. */
  respondToInvitation: (invitationId: string, accept: boolean) => Promise<void>;

  setMemberRole: (projectId: string, userId: string, role: MemberRole) => Promise<void>;
  removeMember: (projectId: string, userId: string) => Promise<void>;
  /** Leave a project I'm a member of (removes my own membership row). */
  leaveProject: (projectId: string) => Promise<void>;

  /** Whether I may edit a shared project's structure (owner or editor). */
  canEdit: (projectId: string) => boolean;

  /** projectId → its task assignments (the member-writable completion rows). */
  assignmentsByProject: Record<string, TaskAssignmentRow[]>;
  fetchAssignments: () => Promise<void>;
  /** Owner/editor: point a task's assignment at a member (or clear it). */
  assignTask: (projectId: string, taskId: string, assignedTo: string) => Promise<void>;
  unassignTask: (projectId: string, taskId: string) => Promise<void>;
  /** Assignee: mark my assigned task done / not-done. */
  completeAssignment: (projectId: string, taskId: string, done: boolean) => Promise<void>;

  /** XP I earned completing assigned tasks, per category id (0217 ledger).
   *  useUserStats folds this into my domain totals (the routine-domain-XP path). */
  myXpByCategory: Record<string, number>;
  fetchMyXp: () => Promise<void>;

  /** Live updates for ONE open shared project (0219 realtime). Refetches
   *  assignments on any assignment change (the owner sees a member's completion
   *  live); with `withEdits`, also refetches shared projects on a content edit
   *  (a member watching the owner's edits). Returns an unsubscribe fn; a no-op
   *  when signed out or the tables aren't on the publication yet. */
  subscribeProject: (projectId: string, withEdits?: boolean) => Promise<() => void>;
  clearStore: () => void;
}

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

/** An action's failure — shown only while the account it ran for is still signed in. */
function failed(at: number, error: unknown) {
  if (at === epoch) useCollaborationStore.setState({ error: (error as Error).message });
}

/** Relational `projects` row (only the columns we read). */
interface ProjectRow {
  revision: number;
  id: string;
  owner_id: string;
  workspace_id: string | null;
  data: Partial<Project> | null;
}

function hydrateProject(row: ProjectRow): Project | null {
  const data = row.data;
  if (!data || Object.keys(data).length === 0) return null; // pre-0214 row — skip
  // A copy shared before 2026-09-16 still carries `milestones`; members must see
  // them as the flagged tasks every surface now reads.
  return convertLegacyMilestones({ ...(data as Project), id: row.id });
}

/** Map a shared Project to the relational row's NOT-NULL columns + the blob. */
function toRow(project: Project, ownerId: string, workspaceId?: string) {
  return {
    id: project.id,
    user_id: ownerId,
    owner_id: ownerId,
    // Do NOT carry the project's OWN (blob) workspaceId — it's a user_projects
    // workspace that doesn't exist in the relational `workspaces` table (that
    // backend is dormant), so it would trip the workspace_id FK and the whole
    // share would fail. A shared project only lives in a relational workspace if
    // one is explicitly passed. (This is why sharing a project inside a workspace
    // silently no-op'd — the insert was rejected.)
    workspace_id: workspaceId ?? null,
    title: project.title,
    description: project.description ?? '',
    category: project.category,
    status: project.status,
    start_date: project.startDate || project.createdAt || new Date().toISOString(),
    due_date: project.dueDate ?? null,
    priority: project.priority,
    complexity: project.complexity,
    progress: project.progress ?? 0,
    is_collaborative: true,
    tags: project.tags ?? [],
    data: sharedProjectData(project),
  };
}

async function loadProfiles(ids: string[]): Promise<Map<string, Profile>> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return new Map();
  const { data } = await supabase
    .from('user_profiles')
    .select('id, name, handle, avatar_url')
    .in('id', unique);
  return new Map(
    (data || []).map(p => [
      p.id,
      { name: p.name || 'Unknown User', handle: p.handle || null, avatar: p.avatar_url || null },
    ])
  );
}

export const useCollaborationStore = create<CollaborationState>((set, get) => ({
  sharedProjects: [],
  sharedMeta: {},
  membersByProject: {},
  assignmentsByProject: {},
  myXpByCategory: {},
  incomingInvitations: [],
  outgoingInvitations: [],
  isLoading: false,
  error: null,

  fetchShared: async () => {
    const at = epoch;
    set({ isLoading: true, error: null });
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) { set({ isLoading: false }); return; }

      const { data: rows, error } = await supabase
        .from('projects')
        .select('id, owner_id, workspace_id, data, revision')
        .order('updated_at', { ascending: false });
      if (isUnmigrated(error)) {
        set({ sharedProjects: [], sharedMeta: {}, membersByProject: {}, isLoading: false });
        return;
      }
      if (error) throw error;

      const projectRows = (rows || []) as ProjectRow[];
      const projects: Project[] = [];
      const meta: Record<string, SharedMeta> = {};
      for (const row of projectRows) {
        const project = hydrateProject(row);
        if (!project) continue;
        projects.push(project);
        meta[row.id] = {
          ownerId: row.owner_id,
          workspaceId: row.workspace_id ?? undefined,
          isOwner: row.owner_id === user.id,
          myRole: row.owner_id === user.id ? 'owner' : 'viewer', // refined from members below
        };
      }

      // Members for all shared projects in one pass, then hydrate profiles.
      const ids = projects.map(p => p.id);
      const membersByProject: Record<string, ProjectMember[]> = {};
      if (ids.length > 0) {
        const { data: memberRows } = await supabase
          .from('project_members')
          .select('id, project_id, user_id, role, invited_by, joined_at')
          .in('project_id', ids);
        const profiles = await loadProfiles((memberRows || []).map(m => m.user_id));
        for (const m of memberRows || []) {
          const profile = profiles.get(m.user_id);
          (membersByProject[m.project_id] ||= []).push({
            id: m.id,
            projectId: m.project_id,
            userId: m.user_id,
            role: m.role as MemberRole,
            invitedBy: m.invited_by ?? undefined,
            joinedAt: m.joined_at,
            displayName: profile?.name,
            handle: profile?.handle ?? undefined,
            avatarUrl: profile?.avatar ?? undefined,
          });
          if (m.user_id === user.id && meta[m.project_id]) {
            meta[m.project_id].myRole = m.role as MemberRole;
          }
        }
      }

      // Signed out during any of the reads: none of it is the next account's.
      if (at !== epoch) return;
      // Leave content alone while reconciling; an in-flight edit owns its optimism.
      set({ sharedProjects: get().sharedProjects.filter(project => projects.some(row => row.id === project.id)), sharedMeta: Object.fromEntries(Object.entries(meta).map(([id, value]) => [id, { ...value, revision: get().sharedMeta[id]?.revision }])), membersByProject, isLoading: false });
      for (const row of (rows ?? []) as ProjectRow[]) {
        // ⚠️⚠️ Reconciling writes the row (as its owner) and mirrors it into the project
        // store — for `user.id`. After a sign-out that is no longer who is signed in.
        if (at !== epoch) return;
        const project = hydrateProject(row);
        if (project) await reconcileSharedRow({ id: row.id, owner_id: row.owner_id, revision: Number(row.revision ?? 0), data: sharedProjectData(project) }, user.id);
      }
      if (at !== epoch) return; // the project store is the next account's now
      await adoptUnsharedCopies(new Set(projects.map(project => project.id)));
    } catch (error) {
      if (at === epoch) set({ error: (error as Error).message, isLoading: false });
    }
  },

  fetchInvitations: async () => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;

      const { data: rows, error } = await supabase
        .from('project_invitations')
        .select('*')
        .order('created_at', { ascending: false });
      if (isUnmigrated(error)) {
        set({ incomingInvitations: [], outgoingInvitations: [] });
        return;
      }
      if (error) throw error;

      const inviterProfiles = await loadProfiles((rows || []).map(r => r.inviter_id));
      const invites: ProjectInvitation[] = (rows || []).map(r => {
        const inviter = inviterProfiles.get(r.inviter_id);
        return {
          id: r.id,
          projectId: r.project_id,
          workspaceId: r.workspace_id ?? undefined,
          inviterId: r.inviter_id,
          inviteeEmail: r.invitee_email,
          inviteeId: r.invitee_id ?? undefined,
          role: r.role as InvitableRole,
          status: r.status,
          message: r.message ?? undefined,
          expiresAt: r.expires_at,
          acceptedAt: r.accepted_at ?? undefined,
          createdAt: r.created_at,
          inviterName: inviter?.name,
          inviterHandle: inviter?.handle ?? undefined,
          inviterAvatar: inviter?.avatar ?? undefined,
        };
      });

      // Signed out during either read: none of it is the next account's.
      if (at !== epoch) return;
      set({
        // RLS returns invites I sent (inviter) and invites to me (invitee id/email).
        outgoingInvitations: invites.filter(i => i.inviterId === user.id),
        incomingInvitations: invites.filter(
          i => i.inviterId !== user.id && i.status === 'pending'
        ),
      });
    } catch (error) {
      failed(at, error);
    }
  },

  shareProject: async (project, workspaceId) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return false;
      const { error } = await supabase
        .from('projects')
        // Insert-only (0264): an existing row is left alone. An UPDATE here would
        // not carry the revision and be refused; the mirror save keeps data current.
        .upsert({ ...toRow(project, user.id, workspaceId), ...(project.privateSince ? { revision: project.privateSince.revision + 1 } : {}) }, { onConflict: 'id', ignoreDuplicates: true });
      if (error) throw error;
      // Shared for the account that signed out meanwhile: its activity is not the next one's to upload.
      if (at !== epoch) return true;
      queueProjectActivity(project.id, user.id, (project.activity ?? []).filter(event => event.actorId === undefined || event.actorId === user.id));
      await get().fetchShared();
      return true;
    } catch (error) {
      failed(at, error);
      return false;
    }
  },

  unshareProject: async (projectId) => { await stopSharing(projectId); },

  invite: async (projectId, email, role, message) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      const { data, error } = await supabase.from('project_invitations').insert({
        project_id: projectId,
        inviter_id: user.id,
        invitee_email: email.trim().toLowerCase(),
        role,
        status: 'pending',
        message: message?.trim() || '',
      }).select('id').single();
      if (error) throw error;
      // Best-effort email so an invitee WITHOUT an account learns of it (existing
      // users already see it in-app via the RLS email match). Never fail the
      // invite on an email error — the in-app invitation already stands.
      if (data?.id) {
        try { await supabase.functions.invoke('send-invitation-email', { body: { invitationId: data.id } }); }
        catch { /* email is optional / not configured */ }
      }
      await get().fetchInvitations();
    } catch (error) {
      failed(at, error);
    }
  },

  inviteMember: async (projectId, userId, email, role) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      const { data, error } = await supabase.from('project_invitations').insert({
        project_id: projectId,
        inviter_id: user.id,
        invitee_id: userId,
        invitee_email: email ? email.trim().toLowerCase() : null,
        role,
        status: 'pending',
        message: '',
      }).select('id').single();
      if (error) throw error;
      // Best-effort email (the edge fn resolves the address from the id if needed);
      // the in-app inbox + the 0218 notification already reached them regardless.
      if (data?.id) {
        try { await supabase.functions.invoke('send-invitation-email', { body: { invitationId: data.id } }); }
        catch { /* email is optional / not configured */ }
      }
      await get().fetchInvitations();
    } catch (error) {
      failed(at, error);
    }
  },

  respondToInvitation: async (invitationId, accept) => {
    const at = epoch;
    try {
      const { error } = await supabase
        .from('project_invitations')
        .update({ status: accept ? 'accepted' : 'rejected' })
        .eq('id', invitationId);
      if (error) throw error;
      await get().fetchInvitations();
      if (accept) await get().fetchShared(); // the trigger created my membership
    } catch (error) {
      failed(at, error);
    }
  },

  setMemberRole: async (projectId, userId, role) => {
    const at = epoch;
    try {
      const { error } = await supabase
        .from('project_members')
        .update({ role })
        .eq('project_id', projectId)
        .eq('user_id', userId);
      if (error) throw error;
      await get().fetchShared();
    } catch (error) {
      failed(at, error);
    }
  },

  removeMember: async (projectId, userId) => {
    const at = epoch;
    try {
      const { error } = await supabase
        .from('project_members')
        .delete()
        .eq('project_id', projectId)
        .eq('user_id', userId);
      if (error) throw error;
      await get().fetchShared();
    } catch (error) {
      failed(at, error);
    }
  },

  leaveProject: async (projectId) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      await supabase.from('project_members').delete()
        .eq('project_id', projectId).eq('user_id', user.id);
      set(state => ({
        sharedProjects: state.sharedProjects.filter(p => p.id !== projectId),
      }));
    } catch (error) {
      failed(at, error);
    }
  },

  canEdit: (projectId) => {
    const role = get().sharedMeta[projectId]?.myRole;
    return role === 'owner' || role === 'editor';
  },

  fetchAssignments: async () => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      const { data, error } = await supabase
        .from('project_task_assignments')
        .select('project_id, task_id, assigned_to, assigned_by, completed_at');
      if (isUnmigrated(error)) { set({ assignmentsByProject: {} }); return; }
      if (error) throw error;
      const byProject: Record<string, TaskAssignmentRow[]> = {};
      for (const r of data || []) {
        (byProject[r.project_id] ||= []).push({
          taskId: r.task_id,
          assignedTo: r.assigned_to,
          assignedBy: r.assigned_by,
          completedAt: r.completed_at ?? null,
        });
      }
      if (at !== epoch) return; // signed out while it read
      set({ assignmentsByProject: byProject });
    } catch (error) {
      failed(at, error);
    }
  },

  assignTask: async (projectId, taskId, assignedTo) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      const { error } = await supabase.from('project_task_assignments').upsert(
        { project_id: projectId, task_id: taskId, assigned_to: assignedTo, assigned_by: user.id },
        { onConflict: 'project_id,task_id' }
      );
      if (error) throw error;
      await get().fetchAssignments();
    } catch (error) {
      failed(at, error);
    }
  },

  unassignTask: async (projectId, taskId) => {
    const at = epoch;
    try {
      const { error } = await supabase.from('project_task_assignments').delete()
        .eq('project_id', projectId).eq('task_id', taskId);
      if (error) throw error;
      await get().fetchAssignments();
    } catch (error) {
      failed(at, error);
    }
  },

  completeAssignment: async (projectId, taskId, done) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      const { error } = await supabase.from('project_task_assignments')
        .update({ completed_at: done ? new Date().toISOString() : null })
        .eq('project_id', projectId).eq('task_id', taskId).eq('assigned_to', user.id);
      if (error) throw error;
      await get().fetchAssignments();
      await get().fetchMyXp(); // the 0217 trigger just awarded/removed the XP
    } catch (error) {
      failed(at, error);
    }
  },

  fetchMyXp: async () => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      const { data, error } = await supabase
        .from('collaboration_task_xp')
        .select('category, points')
        .eq('user_id', user.id);
      if (isUnmigrated(error)) { set({ myXpByCategory: {} }); return; }
      if (error) throw error;
      const byCat: Record<string, number> = {};
      for (const r of data || []) byCat[r.category] = (byCat[r.category] ?? 0) + (r.points || 0);
      if (at !== epoch) return; // signed out while it read: not the next account's XP
      set({ myXpByCategory: byCat });
    } catch (error) {
      failed(at, error);
    }
  },

  subscribeProject: async (projectId, withEdits = false) => {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return () => {};
    // One channel per open project; RLS governs what actually reaches us. Pre-0219
    // (tables not on the publication) this simply receives nothing — harmless.
    let channel = supabase
      .channel(`project-collab-${projectId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'project_task_assignments', filter: `project_id=eq.${projectId}` },
        () => { void get().fetchAssignments(); }
      );
    if (withEdits) {
      channel = channel.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'projects', filter: `id=eq.${projectId}` },
        () => { void get().fetchShared(); }
      );
    }
    channel.subscribe();
    return () => { void supabase.removeChannel(channel); };
  },

  /**
   * Back to empty — run on sign-out (lib/signOutReset). No `persist`, but a sign-out does
   * not reload the page: the next account saw the last one's shared projects, members,
   * invitations and assignment XP. A member change or leave still in flight refetches
   * for the account signed in by then, or maps by id over the emptied lists.
   */
  clearStore: () => {
    epoch += 1;
    set({
      sharedProjects: [], sharedMeta: {}, membersByProject: {}, assignmentsByProject: {}, myXpByCategory: {},
      incomingInvitations: [], outgoingInvitations: [], isLoading: false, error: null,
    });
  },
}));

onSignOut(() => useCollaborationStore.getState().clearStore());
